'use client'

import { Calendar, type DateValue } from '@heroui/calendar'
import { Chip } from '@heroui/chip'
import { useDisclosure } from '@heroui/modal'
import { Popover, PopoverContent, PopoverTrigger } from '@heroui/popover'
import { getLocalTimeZone, parseDate, today } from '@internationalized/date'
import { format, isFuture } from 'date-fns'
import { useEffect } from 'react'

import { SendIcon2 } from '@/components/shared'
import { Typography } from '@/components/ui'

interface CreateMomentDateInputProps {
  value: DateValue
  onChange: (value: DateValue) => void
  mode?: 'create' | 'edit'
  initialDate?: string
}

const CreateMomentDateInput = ({
  value,
  onChange,
  mode = 'create',
  initialDate,
}: CreateMomentDateInputProps) => {
  const localTimeZone = getLocalTimeZone()
  const { isOpen, onOpenChange, onClose } = useDisclosure()
  useEffect(() => {
    if (mode === 'edit' && initialDate) {
      try {
        const date = new Date(initialDate)
        const formattedDate = date.toISOString().split('T')[0]
        onChange(parseDate(formattedDate))
      } catch (error) {
        console.error('Error parsing date:', error)
        onChange(today(localTimeZone))
      }
    } else if (mode === 'create') {
      onChange(today(localTimeZone))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const date = value.toDate(localTimeZone)
  const formattedDate = format(date, 'do MMM, yyyy')
  const isFutureLetter = isFuture(date)

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      classNames={{
        content: 'border-none p-0',
      }}
    >
      <PopoverTrigger>
        <Chip
          className="cursor-pointer"
          variant="solid"
          classNames={{
            base: 'bg-default-100',
          }}
        >
          <div className="flex items-center gap-2">
            <Typography size="small">{formattedDate}</Typography>
            {isFutureLetter && <SendIcon2 className="text-lg" />}
          </div>
        </Chip>
      </PopoverTrigger>
      <PopoverContent className="w-full">
        <Calendar
          aria-label="Date picker"
          value={value}
          onChange={(date) => {
            onChange(date)
            onClose()
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

export default CreateMomentDateInput
